import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LocalDate } from "@/components/ui/local-date";
import { ApiError, api } from "@/lib/api";
import { formatNumber } from "@/lib/i18n";
import { useApiMutation } from "@/lib/use-api-mutation";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * `/accounting/assets` — fixed asset cards.
 *
 * Two depreciation series side by side: the tax one (§31/§32 of the
 * Income Tax Act) and the accounting one. They are not the same and the
 * page deliberately keeps them apart — the tax one goes into the tax
 * return, the accounting one into the statements.
 */

type Asset = {
  id: string;
  number: string;
  name: string;
  cost: string;
  taxGroup: number;
  taxMethod: string;
  accountingMonths: number;
  commissionedOn: string;
  disposedOn: string | null;
  status: string;
  depreciated: string;
  remaining: string;
};

type ScheduleRow = { year: number; ordinal: number; amount: string; remainingValue: string };
type MonthlyRow = { year: number; month: number; amount: string; remainingValue: string };

export const Route = createFileRoute("/_app/accounting/assets")({
  loader: async () => {
    try {
      return await api.get<{ assets: Asset[] }>("/api/accounting/assets");
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: AssetsPage,
});

function money(amount: string): string {
  return formatNumber(Number(amount), undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function AssetsPage() {
  const { t } = useTranslation();
  const { assets } = Route.useLoaderData();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<string | null>(null);

  const thisMonth = new Date();
  const depreciate = useApiMutation(
    () =>
      api.post<{ posted: string[] }>("/api/accounting/assets/depreciate", {
        year:
          thisMonth.getUTCMonth() === 0
            ? thisMonth.getUTCFullYear() - 1
            : thisMonth.getUTCFullYear(),
        month: thisMonth.getUTCMonth() === 0 ? 12 : thisMonth.getUTCMonth(),
      }),
    { onSuccess: () => router.invalidate(), errorMessage: () => t("accAssets.depreciateFailed") },
  );

  const active = assets.filter((a) => a.status !== "disposed");
  const total = active.reduce((sum, a) => sum + Number(a.remaining), 0);

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl">{t("accAssets.title")}</h1>
          <p className="text-muted-foreground text-sm">
            {t("accAssets.subtitle", { value: money(String(total)), count: active.length })}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={depreciate.pending}
            onClick={() => depreciate.mutate()}
          >
            {depreciate.pending ? t("accAssets.posting") : t("accAssets.postLastMonth")}
          </Button>
          <Button onClick={() => setOpen((value) => !value)}>
            {open ? t("accAssets.close") : t("accAssets.newCard")}
          </Button>
        </div>
      </div>

      {depreciate.error && <p className="text-destructive text-sm">{depreciate.error}</p>}

      {open && (
        <AssetForm
          onDone={() => {
            setOpen(false);
            router.invalidate();
          }}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("accAssets.cards")}</CardTitle>
          <CardDescription>{t("accAssets.cardsHint")}</CardDescription>
        </CardHeader>
        <CardContent>
          {assets.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t("accAssets.noCards")}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-4 font-medium">{t("accAssets.number")}</th>
                  <th className="py-1 pr-4 font-medium">{t("accAssets.name")}</th>
                  <th className="py-1 pr-4 font-medium">{t("accAssets.inUseSince")}</th>
                  <th className="py-1 pr-4 font-medium">{t("accAssets.group")}</th>
                  <th className="py-1 pr-4 text-right font-medium">{t("accAssets.cost")}</th>
                  <th className="py-1 pr-4 text-right font-medium">{t("accAssets.depreciated")}</th>
                  <th className="py-1 pr-4 text-right font-medium">{t("accAssets.netValue")}</th>
                  <th className="py-1 font-medium" />
                </tr>
              </thead>
              <tbody>
                {assets.map((asset) => (
                  <tr key={asset.id} className="border-t align-top">
                    <td className="py-1 pr-4 tabular-nums">{asset.number}</td>
                    <td className="py-1 pr-4">
                      {asset.name}
                      {asset.status === "disposed" && (
                        <Badge variant="secondary" className="ml-2">
                          {t("accAssets.disposed")} <LocalDate value={asset.disposedOn ?? ""} />
                        </Badge>
                      )}
                    </td>
                    <td className="py-1 pr-4">
                      <LocalDate value={asset.commissionedOn} />
                    </td>
                    <td className="py-1 pr-4 text-muted-foreground">
                      {asset.taxGroup}. /{" "}
                      {t(`accAssets.method.${asset.taxMethod}`, { defaultValue: asset.taxMethod })}
                    </td>
                    <td className="py-1 pr-4 text-right tabular-nums">{money(asset.cost)}</td>
                    <td className="py-1 pr-4 text-right tabular-nums">
                      {money(asset.depreciated)}
                    </td>
                    <td className="py-1 pr-4 text-right tabular-nums">{money(asset.remaining)}</td>
                    <td className="py-1">
                      <button
                        type="button"
                        className="text-sm underline"
                        onClick={() => setDetail(detail === asset.id ? null : asset.id)}
                      >
                        {t("accAssets.schedule")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {detail && <SchedulePanel assetId={detail} />}
    </div>
  );
}

function SchedulePanel({ assetId }: { assetId: string }) {
  const { t } = useTranslation();
  const [data, setData] = useState<{ tax: ScheduleRow[]; accounting: MonthlyRow[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setData(null);
    api
      .get<{ tax: ScheduleRow[]; accounting: MonthlyRow[] }>(
        `/api/accounting/assets/${assetId}/schedule`,
      )
      .then((result) => live && setData(result))
      .catch(() => live && setError(t("accAssets.scheduleFailed")));
    return () => {
      live = false;
    };
  }, [assetId, t]);

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("accAssets.taxDepreciation")}</CardTitle>
          <CardDescription>{t("accAssets.taxDepreciationHint")}</CardDescription>
        </CardHeader>
        <CardContent>
          {error && <p className="text-destructive text-sm">{error}</p>}
          {data && (
            <table className="w-full text-sm">
              <tbody>
                {data.tax.map((row) => (
                  <tr key={row.year} className="border-t">
                    <td className="py-1 pr-4 tabular-nums">{row.year}</td>
                    <td className="py-1 pr-4 text-right tabular-nums">{money(row.amount)}</td>
                    <td className="py-1 text-right text-muted-foreground tabular-nums">
                      {money(row.remainingValue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("accAssets.accDepreciation")}</CardTitle>
          <CardDescription>{t("accAssets.accDepreciationHint")}</CardDescription>
        </CardHeader>
        <CardContent className="max-h-96 overflow-auto">
          {data && (
            <table className="w-full text-sm">
              <tbody>
                {data.accounting.map((row) => (
                  <tr key={`${row.year}-${row.month}`} className="border-t">
                    <td className="py-1 pr-4 tabular-nums">
                      {row.month}/{row.year}
                    </td>
                    <td className="py-1 pr-4 text-right tabular-nums">{money(row.amount)}</td>
                    <td className="py-1 text-right text-muted-foreground tabular-nums">
                      {money(row.remainingValue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AssetForm({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const create = useApiMutation(
    (body: Record<string, unknown>) => api.post("/api/accounting/assets", body),
    { onSuccess: onDone, errorMessage: () => t("accAssets.form.createFailed") },
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("accAssets.form.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="grid gap-3 md:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            const form = new FormData(e.currentTarget);
            create.mutate({
              number: String(form.get("number")),
              name: String(form.get("name")),
              acquiredOn: String(form.get("acquiredOn")),
              commissionedOn: String(form.get("commissionedOn")),
              cost: Number(form.get("cost")),
              taxGroup: Number(form.get("taxGroup")),
              taxMethod: String(form.get("taxMethod")),
              accountingMonths: Number(form.get("accountingMonths")),
            });
          }}
        >
          <Field name="number" label={t("accAssets.form.cardNumber")} required />
          <Field name="name" label={t("accAssets.form.name")} required />
          <Field name="cost" label={t("accAssets.form.cost")} type="number" step="0.01" required />
          <Field name="acquiredOn" label={t("accAssets.form.acquired")} type="date" required />
          <Field
            name="commissionedOn"
            label={t("accAssets.form.inUseSince")}
            type="date"
            required
          />
          <Field
            name="accountingMonths"
            label={t("accAssets.form.accMonths")}
            type="number"
            defaultValue="36"
            required
          />
          <label className="text-sm">
            <span className="text-muted-foreground text-xs">{t("accAssets.form.group")}</span>
            <select
              name="taxGroup"
              className="mt-1 block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
            >
              {[1, 2, 3, 4, 5, 6].map((group) => (
                <option key={group} value={group}>
                  {t("accAssets.form.groupN", { group })}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="text-muted-foreground text-xs">{t("accAssets.form.taxMethod")}</span>
            <select
              name="taxMethod"
              className="mt-1 block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="straight_line">{t("accAssets.method.straight_line")}</option>
              <option value="accelerated">{t("accAssets.method.accelerated")}</option>
            </select>
          </label>
          <div className="flex items-end">
            <Button type="submit" disabled={create.pending}>
              {create.pending ? t("accAssets.form.creating") : t("accAssets.form.create")}
            </Button>
          </div>
          {create.error && <p className="text-destructive text-sm md:col-span-3">{create.error}</p>}
        </form>
      </CardContent>
    </Card>
  );
}

function Field(props: {
  name: string;
  label: string;
  type?: string;
  step?: string;
  defaultValue?: string;
  required?: boolean;
}) {
  const { label, ...rest } = props;
  return (
    <label className="text-sm">
      <span className="text-muted-foreground text-xs">{label}</span>
      <input
        {...rest}
        className="mt-1 block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
      />
    </label>
  );
}
